import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormDrawer } from '@/components/feedback/drawer';
import { useToast } from '@/components/feedback/toast';
import { SelectField, TextAreaField, UploadField } from '@/components/forms';
import { createPresignedUploader, type UploadedFile } from '@/lib/upload';

import { getOrderPresignedUrl } from '../api';
import { useRequestReplacement } from '../hooks';
import type { OrderDetail } from '../types';

const FOLDER = 'orders/replacements/images/';

const uploadedFileSchema = z.object({
  id: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number(),
  contentType: z.string(),
});

const replacementFormSchema = z.object({
  lineItemId: z.string().min(1, 'Select a device'),
  reason: z.string().min(1, 'Reason is required'),
  images: z.array(uploadedFileSchema),
});
type ReplacementForm = z.infer<typeof replacementFormSchema>;

type Props = { order: OrderDetail; open: boolean; onClose: () => void };

export function RequestReplacementDrawer({ order, open, onClose }: Props) {
  const form = useForm<ReplacementForm>({
    resolver: zodResolver(replacementFormSchema),
    defaultValues: { lineItemId: '', reason: '', images: [] },
    mode: 'onBlur',
  });
  const deviceOptions = useMemo(
    () => order.lineItems.map((li) => ({ value: li.id, label: li.name })),
    [order.lineItems],
  );
  const imageUploader = useMemo(
    () => createPresignedUploader({ getPresignedUrl: (f) => getOrderPresignedUrl(f, FOLDER) }),
    [],
  );
  const replace = useRequestReplacement(order.id);
  const toast = useToast();

  const onSubmit = async (values: ReplacementForm) => {
    try {
      await replace.mutateAsync({
        lineItemId: values.lineItemId,
        reason: values.reason,
        imageUrls: values.images.map((i: UploadedFile) => i.url),
      });
      toast.success({ title: 'Replacement request submitted successfully' });
      onClose();
    } catch (e) {
      toast.error({ title: e instanceof Error ? e.message : 'Could not submit the request.' });
    }
  };

  return (
    <FormDrawer
      form={form}
      onSubmit={onSubmit}
      open={open}
      onClose={onClose}
      title="Request Replacement"
      submitText="Submit"
      width={464}
    >
      <SelectField
        name="lineItemId"
        control={form.control}
        label="Choose Device"
        placeholder="Select"
        required
        options={deviceOptions}
      />
      <TextAreaField
        name="reason"
        control={form.control}
        label="Reason"
        placeholder="Enter"
        required
        rows={4}
      />
      <UploadField
        name="images"
        control={form.control}
        label="Images"
        variant="image"
        onUpload={imageUploader}
        maxCount={5}
        maxSizeMb={5}
        accept="image/jpeg,image/png"
      />
    </FormDrawer>
  );
}
